(() => {
  if (customElements.get('maisonco-bundle-builder')) return;

  class MaisoncoBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      this.variants = JSON.parse(this.querySelector('[data-maisonco-variants]')?.textContent || '[]');
      this.cards = [...this.querySelectorAll('[data-maisonco-style]')];
      this.selection = [];
      this.targetCount = Number(this.dataset.offerCount) === 2 ? 2 : 1;
      this.allowDuplicate = this.dataset.allowDuplicate === 'true';
            this.giftVariantId = Number(this.dataset.giftVariantId) || null;
      this.giftAvailable = this.dataset.giftAvailable === 'true';
      this.giftVariantId2 = Number(this.dataset.giftVariantId2) || null;
      this.giftAvailable2 = this.dataset.giftAvailable2 === 'true';
      this.overlay = this.querySelector('[data-maisonco-overlay]');
      this.drawer = this.querySelector('[data-maisonco-drawer]');
      this.zoomOverlay = this.querySelector('[data-maisonco-zoom-overlay]');
      this.zoomDialog = this.querySelector('[data-maisonco-zoom-dialog]');
      // Product sections can create their own stacking contexts. Keep dialogs at
      // the document root so the store header cannot paint over them.
      this.portal = document.createElement('div');
      this.portal.className = 'maisonco_bundle maisonco_bundle__portal';
      this.portal.setAttribute('style', this.getAttribute('style') || '');
      this.portal.append(this.overlay, this.zoomOverlay);
      document.body.append(this.portal);
      this.portal.addEventListener('click', event => this.handleClick(event), { signal: this._controller.signal });
      this.cards.forEach(card => {
        const requested = card.dataset.variantTitle.trim().toLowerCase();
        card.variant = requested ? this.variants.find(variant => variant.title.toLowerCase().split(' - ')[0] === requested) : null;
        card.bogoVariantId = Number(card.dataset.bogoVariantId) || null;
        card.bogoAvailable = card.dataset.bogoAvailable === 'true';
        const badge = card.querySelector('[data-maisonco-availability]');
        const add = card.querySelector('[data-maisonco-add]');
        if (!card.variant || !card.variant.available) {
          badge.textContent = card.variant ? this.dataset.soldOutLabel : this.dataset.unavailableLabel;
          badge.hidden = false;
          card.querySelector('[data-maisonco-style-badge]')?.setAttribute('hidden', '');
          add.disabled = true;
          card.classList.add('is-unavailable');
        }
      });
      this.addEventListener('click', event => this.handleClick(event), { signal: this._controller.signal });
      document.addEventListener('keydown', event => this.handleKeydown(event), { signal: this._controller.signal });
      document.addEventListener('click', event => {
        if (!event.target.closest('#sticky_atc button[name="add"]')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.selection.length === this.targetCount) this.addToCart();
        else this.openDrawer();
      }, { signal: this._controller.signal, capture: true });
      document.addEventListener('shopify:block:select', event => {
        if (this.contains(event.target) && event.target.closest('[data-maisonco-style]')) this.openDrawer();
      }, { signal: this._controller.signal });
      this.querySelector('[data-maisonco-items]')?.addEventListener('scroll', () => this.updateProgress(), { signal: this._controller.signal, passive: true });
      window.addEventListener('resize', () => this.updateProgress(), { signal: this._controller.signal, passive: true });
      this._visibilityObserver = new IntersectionObserver(entries => {
        document.body.classList.toggle('maisonco_bundle_visible', entries[0].isIntersecting);
      }, { threshold: 0.05 });
      this._visibilityObserver.observe(this);
      this.initItemInfo();
      this.render();
      this.updateProgress();
    }

    disconnectedCallback() {
      this.closeItemInfo();
      this.itemInfo?.remove();
      this.itemInfo = null;
      this._controller?.abort();
      clearTimeout(this._drawerCloseTimer);
      clearTimeout(this._zoomCloseTimer);
      this.portal?.remove();
      this._controller = null;
      this._visibilityObserver?.disconnect();
      document.body.classList.remove('maisonco_bundle_visible');
      document.body.classList.remove('maisonco_bundle_body_locked');
    }

    initItemInfo() {
      const signal = this._controller.signal;
      this.addEventListener('pointerdown', event => {
        this._infoPointer = { x: event.clientX, y: event.clientY };
        this._infoDragged = false;
      }, { signal, passive: true });
      this.addEventListener('pointermove', event => {
        if (this._infoPointer && Math.hypot(event.clientX - this._infoPointer.x, event.clientY - this._infoPointer.y) > 10) {
          this._infoDragged = true;
          this.closeItemInfo();
        }
      }, { signal, passive: true });
      document.addEventListener('pointerup', () => { this._infoPointer = null; }, { signal, passive: true });
      this.addEventListener('pointercancel', () => { this._infoPointer = null; this._infoDragged = true; this.closeItemInfo(); }, { signal });
      document.addEventListener('scroll', event => {
        if (!this.itemInfo?.contains(event.target)) this.closeItemInfo();
      }, { signal, capture: true, passive: true });
      window.addEventListener('resize', () => this.closeItemInfo(), { signal, passive: true });
      document.addEventListener('click', event => {
        if (!event.target.closest('[data-maisonco-info]') && !this.itemInfo?.contains(event.target)) this.closeItemInfo();
      }, { signal });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.itemInfoTrigger) {
          event.preventDefault();
          this.closeItemInfo();
        }
      }, { signal });
      document.addEventListener('focusin', event => {
        if (this.itemInfoTrigger && event.target !== this.itemInfoTrigger) this.closeItemInfo();
      }, { signal });
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-maisonco-info]');
        if (!trigger || !trigger.dataset.maisoncoInfo.trim() || (event.detail !== 0 && this._infoDragged)) return;
        event.preventDefault();
        this.openItemInfo(trigger);
      }, { signal });
    }

    closeItemInfo() {
      if (this.itemInfo) this.itemInfo.hidden = true;
      this.itemInfoTrigger?.setAttribute('aria-expanded', 'false');
      this.itemInfoTrigger?.removeAttribute('aria-describedby');
      this.itemInfoTrigger = null;
    }

    openItemInfo(trigger) {
      const wasOpen = this.itemInfoTrigger === trigger;
      this.closeItemInfo();
      if (wasOpen) return;
      this.itemInfoTrigger = trigger;
      if (!this.itemInfo) {
        this.itemInfo = document.createElement('div');
        this.itemInfo.className = 'maisonco_bundle__item-info';
        this.itemInfo.id = `${this.id}-item-info`;
        this.itemInfo.setAttribute('role', 'tooltip');
        const icon = document.createElement('span');
        icon.className = 'maisonco_bundle__item-info-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = 'i';
        this.itemInfoText = document.createElement('span');
        this.itemInfo.append(icon, this.itemInfoText);
        document.body.append(this.itemInfo);
      }
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-describedby', this.itemInfo.id);
      this.itemInfoText.textContent = trigger.dataset.maisoncoInfo;
      this.itemInfo.hidden = false;
      const rect = trigger.getBoundingClientRect();
      const bubble = this.itemInfo.getBoundingClientRect();
      this.itemInfo.style.left = `${Math.max(12, Math.min(rect.left + rect.width / 2 - bubble.width / 2, innerWidth - bubble.width - 12))}px`;
      this.itemInfo.style.top = `${Math.max(12, rect.top >= bubble.height + 20 ? rect.top - bubble.height - 8 : Math.min(rect.bottom + 8, innerHeight - bubble.height - 12))}px`;
    }

    handleClick(event) {
      const button = event.target.closest('button, [data-maisonco-close], [data-maisonco-zoom-close]');
      if (!button || !(this.contains(button) || this.portal?.contains(button))) return;
      if (button.matches('[data-maisonco-primary]')) {
        if (this.selection.length === this.targetCount) this.addToCart();
        else this.openDrawer();
      } else if (button.matches('[data-maisonco-close]')) {
        this.closeDrawer();
      } else if (button.matches('[data-maisonco-zoom-close]')) {
        this.closeZoom();
      } else if (button.matches('[data-maisonco-scroll]')) {
        const items = this.querySelector('[data-maisonco-items]');
        if (items) {
          const item = items.querySelector('.maisonco_bundle__item');
          const gap = parseFloat(getComputedStyle(items).columnGap) || 0;
          const step = (item?.getBoundingClientRect().width || 80) + gap;
          const max = Math.max(0, items.scrollWidth - items.clientWidth);
          const direction = button.dataset.maisoncoScroll === 'next' ? 1 : -1;
          items.scrollTo({ left: Math.min(max, Math.max(0, items.scrollLeft + direction * step)), behavior: 'smooth' });
        }
      } else if (button.matches('[data-maisonco-empty-slot]')) {
        this.openDrawer();
      } else if (button.matches('[data-maisonco-remove-slot]')) {
        this.selection.splice(Number(button.dataset.maisoncoRemoveSlot), 1);
        this.render();
      } else if (button.matches('[data-maisonco-drawer-slot]')) {
        const index = Number(button.dataset.maisoncoDrawerSlot);
        if (this.selection[index]) this.selection.splice(index, 1);
        this.render();
      } else if (button.matches('[data-maisonco-add], [data-maisonco-increase]')) {
        const card = button.closest('[data-maisonco-style]');
        this.select(card);
      } else if (button.matches('[data-maisonco-decrease]')) {
        const card = button.closest('[data-maisonco-style]');
        const index = this.selection.lastIndexOf(card);
        if (index >= 0) this.selection.splice(index, 1);
        this.render();
      } else if (button.matches('[data-maisonco-zoom]')) {
        this.openZoom(button.closest('[data-maisonco-style]'));
      } else if (button.matches('[data-maisonco-zoom-prev], [data-maisonco-zoom-next]')) {
        this.navigateZoom(button.matches('[data-maisonco-zoom-next]') ? 1 : -1);
      }
    }

    select(card) {
      if (!card?.variant?.available || this.selection.length >= this.targetCount) return;
      if (this.targetCount === 2 && this.selection.length === 1 && (!card.bogoVariantId || !card.bogoAvailable)) return;
      if (!this.allowDuplicate && this.selection.includes(card)) return;
      this.selection.push(card);
      if (this.targetCount === 1) window.variantId2 = card.variant.id;
      this.render();
      if (this.selection.length === this.targetCount) this.closeDrawer();
    }

    render() {
      window.variantId2 = this.selection[0]?.variant.id || this.variants[0]?.id;
      this.renderSlots();
      this.cards.forEach(card => {
        const count = this.selection.filter(selected => selected === card).length;
        card.classList.toggle('is-selected', count > 0);
        const canFillSecondSlot = this.targetCount !== 2 || this.selection.length === 0 || (card.bogoVariantId && card.bogoAvailable);
        card.querySelector('[data-maisonco-add]').disabled = count > 0 || !card.variant?.available || !canFillSecondSlot;
        card.querySelector('[data-maisonco-count]').textContent = String(count);
        card.querySelector('[data-maisonco-increase]').disabled = this.selection.length >= this.targetCount || !this.allowDuplicate || !canFillSecondSlot;
      });
      const primary = this.querySelector('[data-maisonco-primary]');
      const labels = this.querySelector('[data-maisonco-primary-label]');
      labels.textContent = this.selection.length === this.targetCount ? labels.dataset.addLabel : this.targetCount === 2 ? labels.dataset.pickTwoLabel : labels.dataset.pickLabel;
      primary.setAttribute('aria-label', labels.textContent);
      const price = this.querySelector('[data-maisonco-price]');
      price.replaceChildren();
      if (this.dataset.showPrice === 'true') {
        const variant = this.selection[0]?.variant || this.variants[0];
        if (variant) {
          if (this.dataset.showComparePrice === 'true' && variant.compare_at_price > variant.price) {
            const compare = document.createElement('s');
            compare.textContent = this.money(variant.compare_at_price);
            price.append(compare);
          }
          price.append(this.money(variant.price));
        }
      }
      price.hidden = !price.textContent;
      this.querySelector('[data-maisonco-price-separator]').hidden = price.hidden;
      const stock = this.querySelector('[data-maisonco-stock]');
      const available = this.selection.length ? this.selection.every(card => card.variant?.available) : this.cards.some(card => card.variant?.available);
      stock.classList.toggle('is-unavailable', !available);
      stock.querySelector('[data-maisonco-stock-label]').textContent = available ? this.dataset.stockInLabel : this.dataset.stockOutLabel;
    }

    renderSlots() {
      const main = this.querySelector('[data-maisonco-slots]');
      const drawer = this.portal.querySelector('[data-maisonco-drawer-slots]');
      main.replaceChildren();
      drawer.replaceChildren();
      for (let i = 0; i < this.targetCount; i++) {
        const card = this.selection[i];
        const wrap = document.createElement('div');
        wrap.className = 'maisonco_bundle__slot-wrap';
        const slot = document.createElement(card ? 'div' : 'button');
        slot.className = 'maisonco_bundle__slot';
        if (!card) {
          slot.type = 'button';
          slot.dataset.maisoncoEmptySlot = '';
          slot.setAttribute('aria-label', `Choose style ${i + 1}`);
          const plus = document.createElement('span');
          plus.className = 'maisonco_bundle__slot-plus';
          plus.setAttribute('aria-hidden', 'true');
          const plusImage = document.createElement('img');
          plusImage.src = this.dataset.plusIcon;
          plusImage.alt = '';
          plusImage.width = 32;
          plusImage.height = 32;
          plus.append(plusImage);
          slot.append(plus);
        } else {
          const preview = document.createElement('div');
          preview.className = 'maisonco_bundle__slot-preview';
          preview.setAttribute('aria-hidden', 'true');
          this.appendCardImage(preview, card);
          slot.append(preview);
          if (i === 1 && this.targetCount === 2) this.appendFreeBadge(slot);
          const remove = document.createElement('button');
          remove.className = 'maisonco_bundle__slot-remove';
          remove.type = 'button';
          remove.dataset.maisoncoRemoveSlot = String(i);
          remove.setAttribute('aria-label', `Remove ${card.querySelector('.maisonco_bundle__style-name').textContent.trim()}`);
          remove.textContent = '×';
          slot.append(remove);
        }
        if (i === 1 && this.targetCount === 2 && !card) this.appendFreeBadge(slot);
        wrap.append(slot);
        const label = document.createElement('span');
        label.className = 'maisonco_bundle__slot-label';
        label.textContent = card ? card.querySelector('.maisonco_bundle__style-name').textContent.trim() : `Style ${i + 1}`;
        wrap.append(label);
        if (card) {
          const lengths = card.querySelector('.maisonco_bundle__style-lengths');
          if (lengths) {
            const small = document.createElement('span');
            small.className = 'maisonco_bundle__slot-lengths';
            small.textContent = lengths.textContent.trim();
            wrap.append(small);
          }
        }
        main.append(wrap);

        const miniWrap = document.createElement('div');
        miniWrap.className = 'maisonco_bundle__drawer-slot-wrap';
        const mini = document.createElement(card ? 'button' : 'div');
        mini.className = 'maisonco_bundle__drawer-slot';
        if (card) {
          mini.type = 'button';
          mini.dataset.maisoncoDrawerSlot = String(i);
          mini.setAttribute('aria-label', `Remove style ${i + 1}`);
          this.appendCardImage(mini, card);
          if (i === 1 && this.targetCount === 2) this.appendFreeBadge(mini);
        } else {
          mini.setAttribute('aria-hidden', 'true');
          const plusImage = document.createElement('img');
          plusImage.src = this.dataset.smallPlusIcon;
          plusImage.className = 'maisonco_bundle__mini-plus';
          plusImage.alt = '';
          plusImage.width = 24;
          plusImage.height = 24;
          mini.append(plusImage);
        }
        if (i === 1 && this.targetCount === 2 && !card) this.appendFreeBadge(mini);
        miniWrap.append(mini);
        const miniLabel = document.createElement('span');
        miniLabel.className = 'maisonco_bundle__drawer-slot-label';
        miniLabel.textContent = card ? card.querySelector('.maisonco_bundle__style-name').textContent.trim() : `Style ${i + 1}`;
        miniWrap.append(miniLabel);
        drawer.append(miniWrap);
      }
    }

    appendCardImage(container, card) {
      const source = this.cardImage(card);
      if (!source) return;
      const img = document.createElement('img');
      img.src = source.currentSrc || source.src;
      img.alt = source.alt;
      container.append(img);
    }

    cardImage(card) {
      return card?.querySelector('.maisonco_bundle__style-image > img, .maisonco_bundle__style-image > picture img');
    }

    appendFreeBadge(container) {
      const badge = document.createElement('span');
      badge.className = 'maisonco_bundle__slot-free';
      badge.textContent = this.dataset.freeLabel;
      container.append(badge);
    }

    openDrawer() {
      this.closeItemInfo();
      this._previousFocus = document.activeElement;
      clearTimeout(this._drawerCloseTimer);
      this.overlay.hidden = false;
      requestAnimationFrame(() => this.overlay.classList.add('is-open'));
      document.body.classList.add('maisonco_bundle_body_locked');
      this.drawer.focus();
    }

    closeDrawer() {
      this.closeZoom();
      this.overlay.classList.remove('is-open');
      this._drawerCloseTimer = setTimeout(() => { this.overlay.hidden = true; }, 320);
      document.body.classList.remove('maisonco_bundle_body_locked');
      this._previousFocus?.focus?.();
    }

    openZoom(card) {
      if (!this.cardImage(card)) return;
      this._zoomPreviousFocus = document.activeElement;
      this.zoomIndex = this.cards.indexOf(card);
      this.showZoomImage();
      clearTimeout(this._zoomCloseTimer);
      this.zoomOverlay.hidden = false;
      requestAnimationFrame(() => this.zoomOverlay.classList.add('is-open'));
      this.zoomDialog.focus();
    }

    navigateZoom(direction) {
      for (let i = 0; i < this.cards.length; i++) {
        this.zoomIndex = (this.zoomIndex + direction + this.cards.length) % this.cards.length;
        if (this.cardImage(this.cards[this.zoomIndex]) && this.cards[this.zoomIndex].getClientRects().length) break;
      }
      this.showZoomImage();
    }

    showZoomImage() {
      const card = this.cards[this.zoomIndex];
      const source = this.cardImage(card);
      const image = this.portal.querySelector('[data-maisonco-zoom-image]');
      image.src = card?.dataset.zoomImage || source?.currentSrc || source?.src || '';
      image.alt = source?.alt || card?.querySelector('.maisonco_bundle__style-name')?.textContent.trim() || '';
    }

    closeZoom() {
      if (this.zoomOverlay.hidden) return;
      this.zoomOverlay.classList.remove('is-open');
      this._zoomCloseTimer = setTimeout(() => { this.zoomOverlay.hidden = true; }, 280);
      this._zoomPreviousFocus?.focus?.();
    }

    handleKeydown(event) {
      if (this.zoomOverlay.classList.contains('is-open') && event.key === 'Escape') {
        event.preventDefault(); this.closeZoom(); return;
      }
      if (this.overlay.classList.contains('is-open') && event.key === 'Escape') {
        event.preventDefault(); this.closeDrawer(); return;
      }
      const activeDialog = this.zoomOverlay.classList.contains('is-open') ? this.zoomDialog : this.overlay.classList.contains('is-open') ? this.drawer : null;
      if (activeDialog && event.key === 'Tab') {
        const focusable = [...activeDialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled])')].filter(el => el.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === activeDialog)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }

    money(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
      } catch (_) {
        return typeof window.formatMoney === 'function' ? window.formatMoney(cents) : String((cents / 100).toFixed(2));
      }
    }

    async addToCart() {
      const primary = this.querySelector('[data-maisonco-primary]');
      const label = this.querySelector('[data-maisonco-primary-label]');
      const error = this.querySelector('[data-maisonco-error]');
      error.hidden = true;
      primary.disabled = true;
      const oldLabel = label.textContent;
      label.textContent = this.dataset.loadingLabel;
      const items = this.targetCount === 2
        ? [
          { id: this.selection[0]?.variant.id, quantity: 1, properties: { 'bogo.offer': 'true' } },
          { id: this.selection[1]?.bogoVariantId, quantity: 1, properties: { 'bogo.offer': 'true' } }
        ]
        : [{ id: this.selection[0]?.variant.id, quantity: 1 }];
              if (this.targetCount === 1 && this.giftVariantId && this.giftAvailable) {
        items.push({ id: this.giftVariantId, quantity: 1, properties: { 'bogo.offer': 'true' } });
      }
      if (this.targetCount === 1 && this.giftVariantId2 && this.giftAvailable2 && !(this.giftAvailable && this.giftVariantId2 === this.giftVariantId)) {
        items.push({ id: this.giftVariantId2, quantity: 1, properties: { 'bogo.offer': 'true' } });
      }
      const drawer = document.querySelector('cart-drawer');
      try {
        if (this.selection.length !== this.targetCount || items.some(item => !item.id)) throw new Error(this.dataset.errorLabel);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items,
            sections: drawer?.getSectionsToRender?.().map(section => section.id) || []
          })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.errorLabel);
        if (drawer) {
          drawer.renderContents(result);
          drawer.open();
          drawer.classList.remove('is-empty');
          document.documentElement.classList.add('js-ajax-cart-drawer-opened');
        } else {
          document.dispatchEvent(new Event('instant:add-to-cart'));
        }
      } catch (exception) {
        error.textContent = exception.message || this.dataset.errorLabel;
        error.hidden = false;
      } finally {
        primary.disabled = false;
        label.textContent = oldLabel;
      }
    }

    updateProgress() {
      const items = this.querySelector('[data-maisonco-items]');
      const progress = this.querySelector('[data-maisonco-progress]');
      if (!items || !progress) return;
      const maxScroll = Math.max(0, items.scrollWidth - items.clientWidth);
      const previous = this.querySelector('[data-maisonco-scroll="prev"]');
      const next = this.querySelector('[data-maisonco-scroll="next"]');
      if (previous) previous.disabled = items.scrollLeft <= 1;
      if (next) next.disabled = items.scrollLeft >= maxScroll - 1;
      const track = progress.parentElement.clientWidth;
      const thumb = Math.min(track, track * items.clientWidth / (items.scrollWidth || 1));
      progress.style.width = `${thumb}px`;
      progress.style.transform = `translateX(${maxScroll ? items.scrollLeft / maxScroll * (track - thumb) : 0}px)`;
    }
  }

  customElements.define('maisonco-bundle-builder', MaisoncoBundleBuilder);
})();
